/**
 * Material DNA Compare Page
 * ==========================
 * SlabReality - AI Material Intelligence Comparison
 * 
 * Premium side-by-side DNA comparison with:
 * - Radar chart visualization
 * - Compatibility analysis
 * - Emotional profile matching
 * - Application intelligence
 * 
 * Design: Dark luxury, gold accents, architectural grade
 * 
 * @author SlabReality Team
 * @version 1.0
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Search, X, Plus, Sparkles, Target, Waves, 
  Diamond, Activity, Thermometer, Zap, Layers, Heart,
  Building2, Home, Store, Droplets, Mountain, Grid3X3
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

// =============================================================================
// RADAR CHART COMPONENT
// =============================================================================
const DNARadarChart = ({ data1, data2, labels }) => {
  const size = 280;
  const center = size / 2;
  const radius = 110;
  const levels = 5;
  
  // Calculate points for a value set
  const calculatePoints = (values) => {
    const angleStep = (Math.PI * 2) / labels.length;
    return values.map((value, i) => {
      const angle = angleStep * i - Math.PI / 2;
      const r = (value / 100) * radius;
      return {
        x: center + r * Math.cos(angle),
        y: center + r * Math.sin(angle)
      };
    });
  };
  
  const points1 = calculatePoints(data1);
  const points2 = calculatePoints(data2);
  
  // Create SVG path from points
  const createPath = (points) => {
    if (points.length === 0) return '';
    return points.map((p, i) => 
      (i === 0 ? 'M' : 'L') + `${p.x.toFixed(2)},${p.y.toFixed(2)}`
    ).join(' ') + ' Z';
  };
  
  // Grid levels
  const gridLevels = Array.from({ length: levels }, (_, i) => ((i + 1) / levels) * radius);
  
  // Axis lines
  const angleStep = (Math.PI * 2) / labels.length;
  
  return (
    <div className="relative">
      <svg width={size} height={size} className="overflow-visible">
        {/* Grid circles */}
        {gridLevels.map((r, i) => (
          <circle
            key={`grid-${i}`}
            cx={center}
            cy={center}
            r={r}
            fill="none"
            stroke="rgba(200, 163, 106, 0.1)"
            strokeWidth="1"
          />
        ))}
        
        {/* Axis lines */}
        {labels.map((_, i) => {
          const angle = angleStep * i - Math.PI / 2;
          return (
            <line
              key={`axis-${i}`}
              x1={center}
              y1={center}
              x2={center + radius * Math.cos(angle)}
              y2={center + radius * Math.sin(angle)}
              stroke="rgba(200, 163, 106, 0.15)"
              strokeWidth="1"
            />
          );
        })}
        
        {/* Data area 2 (background) */}
        <path
          d={createPath(points2)}
          fill="rgba(100, 149, 237, 0.15)"
          stroke="rgba(100, 149, 237, 0.6)"
          strokeWidth="2"
          className="transition-all duration-700"
        />
        
        {/* Data area 1 (foreground) */}
        <path
          d={createPath(points1)}
          fill="rgba(200, 163, 106, 0.2)"
          stroke="#C8A36A"
          strokeWidth="2"
          className="transition-all duration-700"
        />
        
        {/* Data points 1 */}
        {points1.map((point, i) => (
          <circle
            key={`point1-${i}`}
            cx={point.x}
            cy={point.y}
            r="4"
            fill="#C8A36A"
            className="transition-all duration-500"
          />
        ))}
        
        {/* Data points 2 */}
        {points2.map((point, i) => (
          <circle
            key={`point2-${i}`}
            cx={point.x}
            cy={point.y}
            r="4"
            fill="rgba(100, 149, 237, 0.8)"
            className="transition-all duration-500"
          />
        ))}
        
        {/* Labels */}
        {labels.map((label, i) => {
          const angle = angleStep * i - Math.PI / 2;
          const labelRadius = radius + 25;
          const x = center + labelRadius * Math.cos(angle);
          const y = center + labelRadius * Math.sin(angle);
          
          return (
            <text
              key={`label-${i}`}
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-[#F5F1E8]/60 text-[10px] uppercase tracking-wider"
            >
              {label}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

// =============================================================================
// COMPATIBILITY BAR COMPONENT
// =============================================================================
const CompatibilityBar = ({ label, value, icon: Icon }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-[#C8A36A]/70" />}
        <span className="text-xs uppercase tracking-wider text-[#F5F1E8]/60">{label}</span>
      </div>
      <span className="text-sm font-medium text-[#C8A36A]">{value}%</span>
    </div>
    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
      <div 
        className="h-full rounded-full transition-all duration-1000 ease-out"
        style={{ 
          width: `${value}%`,
          background: value > 80 
            ? 'linear-gradient(90deg, #22c55e 0%, #4ade80 100%)'
            : value > 60
            ? 'linear-gradient(90deg, #C8A36A 0%, #D4AF37 100%)'
            : 'linear-gradient(90deg, #ef4444 0%, #f87171 100%)'
        }}
      />
    </div>
  </div>
);

// =============================================================================
// MATERIAL SELECTOR CARD
// =============================================================================
const MaterialSelectorCard = ({ material, onRemove, onSelect, position, isLoading }) => {
  if (!material) {
    return (
      <div 
        onClick={onSelect}
        className="h-full min-h-[400px] bg-white/[0.02] border-2 border-dashed border-[#C8A36A]/30 rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:border-[#C8A36A]/50 hover:bg-white/[0.03] transition-all group"
      >
        <div className="w-16 h-16 rounded-full bg-[#C8A36A]/10 flex items-center justify-center mb-4 group-hover:bg-[#C8A36A]/20 transition-all">
          <Plus className="w-8 h-8 text-[#C8A36A]" />
        </div>
        <span className="text-[#F5F1E8]/60 text-sm">Seleziona Materiale {position}</span>
      </div>
    );
  }
  
  return (
    <div className="h-full bg-white/[0.03] border border-[#C8A36A]/10 rounded-2xl overflow-hidden">
      {/* Image */}
      <div className="relative aspect-[4/3]">
        <img 
          src={material.image_main || material.image_url || '/placeholder-material.jpg'}
          alt={material.name}
          className="w-full h-full object-cover"
        />
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors"
        >
          <X className="w-4 h-4 text-white" />
        </button>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
          <span className="text-xs uppercase tracking-wider text-[#C8A36A]">
            {material.stone_type || material.category || 'Pietra'}
          </span>
          <h3 className="text-lg font-serif text-white">{material.name}</h3>
        </div>
      </div>
      
      {/* DNA Summary */}
      {material.dna && (
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-white/[0.03] rounded-lg p-2">
              <div className="text-[10px] uppercase text-[#F5F1E8]/40">Tono</div>
              <div className="text-sm text-[#F5F1E8]">{material.dna.dominant_tone || 'N/A'}</div>
            </div>
            <div className="bg-white/[0.03] rounded-lg p-2">
              <div className="text-[10px] uppercase text-[#F5F1E8]/40">Temperatura</div>
              <div className="text-sm text-[#F5F1E8]">{material.dna.temperature || 'N/A'}</div>
            </div>
          </div>
          
          {/* Color Palette */}
          {material.dna.palette && (
            <div className="flex gap-1">
              {material.dna.palette.slice(0, 5).map((color, i) => (
                <div 
                  key={i}
                  className="w-6 h-6 rounded-full border border-white/10"
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          )}
        </div>
      )}
      
      {isLoading && (
        <div className="p-4 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#C8A36A]/30 border-t-[#C8A36A] rounded-full animate-spin" />
          <span className="ml-2 text-sm text-[#F5F1E8]/60">Analizzando DNA...</span>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// APPLICATION INTELLIGENCE CARD
// =============================================================================
const ApplicationCard = ({ icon: Icon, title, score, recommended }) => (
  <div className={`p-3 rounded-xl border transition-all ${
    recommended 
      ? 'bg-[#C8A36A]/10 border-[#C8A36A]/30' 
      : 'bg-white/[0.02] border-white/5'
  }`}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
        recommended ? 'bg-[#C8A36A]/20' : 'bg-white/5'
      }`}>
        <Icon className={`w-5 h-5 ${recommended ? 'text-[#C8A36A]' : 'text-[#F5F1E8]/40'}`} />
      </div>
      <div className="flex-1">
        <div className="text-sm text-[#F5F1E8]">{title}</div>
        {recommended && (
          <div className="text-xs text-[#C8A36A]">Consigliato</div>
        )}
      </div>
      {score !== undefined && (
        <div className={`text-sm font-medium ${recommended ? 'text-[#C8A36A]' : 'text-[#F5F1E8]/40'}`}>
          {score}%
        </div>
      )}
    </div>
  </div>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const MaterialDNACompare = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // State
  const [material1, setMaterial1] = useState(null);
  const [material2, setMaterial2] = useState(null);
  const [dna1, setDna1] = useState(null);
  const [dna2, setDna2] = useState(null);
  const [loading1, setLoading1] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [showSearch, setShowSearch] = useState(null); // 1 or 2
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [compatibility, setCompatibility] = useState(null);
  
  // Load materials from URL params
  useEffect(() => {
    const mat1Id = searchParams.get('mat1');
    const mat2Id = searchParams.get('mat2');
    
    if (mat1Id) loadMaterial(mat1Id, 1);
    if (mat2Id) loadMaterial(mat2Id, 2);
  }, []);
  
  // Load material and analyze DNA
  const loadMaterial = async (materialId, position) => {
    const setLoading = position === 1 ? setLoading1 : setLoading2;
    const setMaterial = position === 1 ? setMaterial1 : setMaterial2;
    const setDna = position === 1 ? setDna1 : setDna2;
    
    setLoading(true);
    
    try {
      // Fetch material
      const matRes = await axios.get(`${API}/api/materials/${materialId}`);
      const mat = matRes.data;
      setMaterial(mat);
      
      // Analyze DNA
      const imageUrl = mat.image_main || mat.image_url;
      if (imageUrl) {
        const fullUrl = imageUrl.startsWith('http') ? imageUrl : `${API}${imageUrl}`;
        const imgRes = await fetch(fullUrl);
        const blob = await imgRes.blob();
        
        const reader = new FileReader();
        reader.onload = async () => {
          const base64 = reader.result.split(',')[1];
          
          try {
            const dnaRes = await axios.post(`${API}/api/material-dna/analyze`, {
              image_base64: base64,
              include_matches: false
            });
            
            const dnaData = dnaRes.data;
            setDna(dnaData);
            setMaterial(prev => ({ ...prev, dna: dnaData }));
          } catch (err) {
            console.error('DNA analysis error:', err);
          }
          setLoading(false);
        };
        reader.readAsDataURL(blob);
      } else {
        setLoading(false);
      }
      
      // Update URL
      const newParams = new URLSearchParams(searchParams);
      newParams.set(`mat${position}`, materialId);
      setSearchParams(newParams);
      
    } catch (err) {
      console.error('Load material error:', err);
      toast.error('Errore caricamento materiale');
      setLoading(false);
    }
  };
  
  // Search materials
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const res = await axios.get(`${API}/api/materials`, {
        params: { search: searchQuery, limit: 12 }
      });
      setSearchResults(res.data.materials || res.data || []);
    } catch (err) {
      console.error('Search error:', err);
    }
    setSearching(false);
  };
  
  // Calculate compatibility when both DNAs are available
  useEffect(() => {
    if (dna1 && dna2) {
      calculateCompatibility();
    }
  }, [dna1, dna2]);
  
  const calculateCompatibility = () => {
    if (!dna1 || !dna2) return;
    
    // Tone compatibility (based on warmth/temperature)
    const warmthDiff = Math.abs((dna1.warmth || 50) - (dna2.warmth || 50));
    const toneCompatibility = Math.max(0, 100 - warmthDiff * 1.5);
    
    // Vein compatibility
    const veinDiff = Math.abs((dna1.vein_movement || 50) - (dna2.vein_movement || 50));
    const veinCompatibility = Math.max(0, 100 - veinDiff * 1.2);
    
    // Contrast harmony
    const contrastDiff = Math.abs((dna1.contrast_energy || 50) - (dna2.contrast_energy || 50));
    const contrastHarmony = Math.max(0, 100 - contrastDiff);
    
    // Texture density match
    const busyDiff = Math.abs((dna1.busy_level || 50) - (dna2.busy_level || 50));
    const textureMatch = Math.max(0, 100 - busyDiff);
    
    // Crystal similarity
    const crystalDiff = Math.abs((dna1.crystalization || 50) - (dna2.crystalization || 50));
    const crystalMatch = Math.max(0, 100 - crystalDiff);
    
    // Overall compatibility
    const overall = (toneCompatibility + veinCompatibility + contrastHarmony + textureMatch + crystalMatch) / 5;
    
    // Emotional profiles
    const getEmotionalProfile = (dna) => {
      const warmth = dna.warmth || 50;
      const contrast = dna.contrast_energy || 50;
      const vein = dna.vein_movement || 50;
      
      if (warmth > 70 && contrast < 40) return "Natural Warmth";
      if (contrast > 70 && vein > 60) return "Dramatic Statement";
      if (warmth < 40 && contrast < 40) return "Minimal Elegance";
      if (vein < 30 && contrast < 50) return "Calm Luxury";
      return "Organic Contemporary";
    };
    
    // Application recommendations based on combined DNA
    const avgVein = ((dna1.vein_movement || 50) + (dna2.vein_movement || 50)) / 2;
    const avgContrast = ((dna1.contrast_energy || 50) + (dna2.contrast_energy || 50)) / 2;
    const avgWarmth = ((dna1.warmth || 50) + (dna2.warmth || 50)) / 2;
    
    const applications = [
      { id: 'waterfall', title: 'Waterfall Islands', score: veinCompatibility > 70 ? 90 : 60, icon: Waves },
      { id: 'spa', title: 'Spa Environments', score: avgWarmth > 60 && avgContrast < 50 ? 95 : 50, icon: Droplets },
      { id: 'hospitality', title: 'Hospitality', score: overall > 75 ? 85 : 55, icon: Building2 },
      { id: 'kitchen', title: 'Statement Kitchens', score: avgContrast > 50 ? 90 : 60, icon: Home },
      { id: 'cladding', title: 'Vertical Cladding', score: avgVein > 50 ? 85 : 50, icon: Layers },
      { id: 'retail', title: 'Luxury Retail', score: overall > 70 ? 90 : 60, icon: Store }
    ];
    
    // Sort by score
    applications.sort((a, b) => b.score - a.score);
    
    setCompatibility({
      overall: Math.round(overall),
      toneCompatibility: Math.round(toneCompatibility),
      veinCompatibility: Math.round(veinCompatibility),
      contrastHarmony: Math.round(contrastHarmony),
      textureMatch: Math.round(textureMatch),
      crystalMatch: Math.round(crystalMatch),
      emotional1: getEmotionalProfile(dna1),
      emotional2: getEmotionalProfile(dna2),
      applications
    });
  };
  
  // Radar chart data
  const radarLabels = ['Venature', 'Energia', 'Cristalli', 'Contrasto', 'Texture', 'Calore'];
  const radarData1 = dna1 ? [
    dna1.vein_movement || 0,
    50, // Visual energy derived from other metrics
    dna1.crystalization || 0,
    dna1.contrast_energy || 0,
    dna1.busy_level || 0,
    dna1.warmth || 0
  ] : [0, 0, 0, 0, 0, 0];
  
  const radarData2 = dna2 ? [
    dna2.vein_movement || 0,
    50,
    dna2.crystalization || 0,
    dna2.contrast_energy || 0,
    dna2.busy_level || 0,
    dna2.warmth || 0
  ] : [0, 0, 0, 0, 0, 0];
  
  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-[#C8A36A] to-[#D4AF37] rounded-lg flex items-center justify-center">
              <Diamond className="w-4 h-4 text-black" />
            </div>
            <span className="text-[#F5F1E8] font-semibold tracking-tight">SlabReality</span>
          </Link>
          
          <h1 className="text-lg font-serif text-[#F5F1E8]">
            Material DNA™ <span className="text-[#C8A36A]">Compare</span>
          </h1>
          
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#F5F1E8]/60 hover:text-[#C8A36A] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Indietro</span>
          </button>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Three Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* LEFT - Material 1 */}
          <div className="lg:col-span-3">
            <MaterialSelectorCard
              material={material1}
              onRemove={() => { setMaterial1(null); setDna1(null); }}
              onSelect={() => setShowSearch(1)}
              position="A"
              isLoading={loading1}
            />
          </div>
          
          {/* CENTER - Comparison Intelligence */}
          <div className="lg:col-span-6 space-y-6">
            {/* Radar Chart */}
            <div className="bg-white/[0.03] border border-[#C8A36A]/10 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <Sparkles className="w-5 h-5 text-[#C8A36A]" />
                <h2 className="text-lg font-medium text-[#F5F1E8]">DNA Comparison</h2>
              </div>
              
              {dna1 && dna2 ? (
                <div className="flex flex-col items-center">
                  <DNARadarChart 
                    data1={radarData1}
                    data2={radarData2}
                    labels={radarLabels}
                  />
                  
                  {/* Legend */}
                  <div className="flex gap-6 mt-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-[#C8A36A]" />
                      <span className="text-xs text-[#F5F1E8]/60">{material1?.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-[rgba(100,149,237,0.8)]" />
                      <span className="text-xs text-[#F5F1E8]/60">{material2?.name}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center">
                  <p className="text-[#F5F1E8]/40 text-sm">Seleziona due materiali per confrontare il DNA</p>
                </div>
              )}
            </div>
            
            {/* Compatibility Metrics */}
            {compatibility && (
              <div className="bg-white/[0.03] border border-[#C8A36A]/10 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <Target className="w-5 h-5 text-[#C8A36A]" />
                    <h2 className="text-lg font-medium text-[#F5F1E8]">Compatibility Analysis</h2>
                  </div>
                  <div className="text-3xl font-serif text-[#C8A36A]">{compatibility.overall}%</div>
                </div>
                
                <div className="space-y-4">
                  <CompatibilityBar label="Tone Compatibility" value={compatibility.toneCompatibility} icon={Thermometer} />
                  <CompatibilityBar label="Vein Compatibility" value={compatibility.veinCompatibility} icon={Waves} />
                  <CompatibilityBar label="Contrast Harmony" value={compatibility.contrastHarmony} icon={Activity} />
                  <CompatibilityBar label="Texture Match" value={compatibility.textureMatch} icon={Grid3X3} />
                  <CompatibilityBar label="Crystal Similarity" value={compatibility.crystalMatch} icon={Diamond} />
                </div>
              </div>
            )}
            
            {/* Emotional Profiles */}
            {compatibility && (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/[0.03] border border-[#C8A36A]/10 rounded-xl p-4">
                  <div className="text-xs uppercase tracking-wider text-[#F5F1E8]/40 mb-2">Emotional Profile A</div>
                  <div className="text-lg font-serif text-[#C8A36A]">{compatibility.emotional1}</div>
                </div>
                <div className="bg-white/[0.03] border border-[#C8A36A]/10 rounded-xl p-4">
                  <div className="text-xs uppercase tracking-wider text-[#F5F1E8]/40 mb-2">Emotional Profile B</div>
                  <div className="text-lg font-serif text-[rgba(100,149,237,0.9)]">{compatibility.emotional2}</div>
                </div>
              </div>
            )}
            
            {/* Application Intelligence */}
            {compatibility && (
              <div className="bg-white/[0.03] border border-[#C8A36A]/10 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Zap className="w-5 h-5 text-[#C8A36A]" />
                  <h2 className="text-lg font-medium text-[#F5F1E8]">Application Intelligence</h2>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {compatibility.applications.map((app, i) => (
                    <ApplicationCard
                      key={app.id}
                      icon={app.icon}
                      title={app.title}
                      score={app.score}
                      recommended={i < 2}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* RIGHT - Material 2 */}
          <div className="lg:col-span-3">
            <MaterialSelectorCard
              material={material2}
              onRemove={() => { setMaterial2(null); setDna2(null); }}
              onSelect={() => setShowSearch(2)}
              position="B"
              isLoading={loading2}
            />
          </div>
        </div>
      </main>
      
      {/* Search Modal */}
      {showSearch && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0A0A0A] border border-[#C8A36A]/20 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            {/* Search Header */}
            <div className="p-4 border-b border-white/5">
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#F5F1E8]/40" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Cerca materiale..."
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-[#F5F1E8] placeholder:text-[#F5F1E8]/40 focus:outline-none focus:border-[#C8A36A]/50"
                    autoFocus
                  />
                </div>
                <button
                  onClick={handleSearch}
                  disabled={searching}
                  className="px-6 py-3 bg-[#C8A36A] text-black rounded-xl font-medium hover:bg-[#D4AF37] transition-colors disabled:opacity-50"
                >
                  {searching ? 'Cercando...' : 'Cerca'}
                </button>
                <button
                  onClick={() => { setShowSearch(null); setSearchResults([]); setSearchQuery(''); }}
                  className="p-3 text-[#F5F1E8]/60 hover:text-[#F5F1E8]"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Results */}
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              {searchResults.length > 0 ? (
                <div className="grid grid-cols-3 gap-3">
                  {searchResults.map((mat) => (
                    <div
                      key={mat.id || mat.source_id}
                      onClick={() => {
                        loadMaterial(mat.id || mat.source_id, showSearch);
                        setShowSearch(null);
                        setSearchResults([]);
                        setSearchQuery('');
                      }}
                      className="cursor-pointer rounded-xl overflow-hidden border border-white/10 hover:border-[#C8A36A]/50 transition-all group"
                    >
                      <div className="aspect-square relative">
                        <img 
                          src={mat.image_main || mat.image_url || '/placeholder-material.jpg'}
                          alt={mat.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      </div>
                      <div className="p-2">
                        <div className="text-xs text-[#C8A36A] uppercase">{mat.stone_type || mat.category}</div>
                        <div className="text-sm text-[#F5F1E8] truncate">{mat.name}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : searchQuery && !searching ? (
                <p className="text-center text-[#F5F1E8]/40 py-8">Nessun risultato per "{searchQuery}"</p>
              ) : (
                <p className="text-center text-[#F5F1E8]/40 py-8">Cerca un materiale da confrontare</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialDNACompare;
